import { isEmpty } from "underscore";
import { makeRequest } from "./AxiosInstance";
import moment from "moment-timezone";
import * as Sentry from "@sentry/react";

export const sleep = (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms));
};

export function handleError(error, context = {}) {
    let errorMessage = "An unknown error occurred";
    console.error(error)
    try {
        Sentry.captureException(error, {
            extra: context,
        });
    } catch (sentryError) {
        console.error("Failed to send error to Sentry:", sentryError);
    }

    if (typeof error === 'string') {
        errorMessage = error;
    } else if (error && typeof error === 'object') {
        if (error.response && error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
        } else if (error.message) {
            errorMessage = error.message;
        } else {
            errorMessage = 'Sorry, there is an error.';
        }
    }
    return errorMessage
    // throw error
};

export const formatDate = (dateString) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
};

export const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

export const convertToLocalTime = (utcDateString) => {
    const date = new Date(utcDateString);
    return date.toLocaleString();
};

export const getPaymentLink = async (id) => {
    return makeRequest('GET', `/get-project-payment-link/${id}`).then((response) => {
        console.log(response);
        return response.data.data;
    })
}

export function formatDateForApi(date) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`;
}

export const formatDateTime = (dateString) => {
    const dateOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const timeOptions = { hour: 'numeric', minute: 'numeric', second: 'numeric' };

    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('en-US', dateOptions);
    const formattedTime = date.toLocaleTimeString('en-US', timeOptions);

    return `${formattedDate} ${formattedTime}`;
};

export function formatDateFromApi(dateString) {
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const nth = (d) => {
        if (d > 3 && d < 21) return 'th'; // special case for numbers between 11 and 20
        switch (d % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    };

    const d = new Date(dateString);
    const dayOfWeek = daysOfWeek[d.getDay()];
    const day = d.getDate();
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12 || 12; // Convert to 12-hour format

    return `${dayOfWeek} ${day}${nth(day)} ${month}, ${year}. ${hours}:${minutes}${ampm}`;
}

export function formatFileName(fileName) {
    if (isEmpty(fileName)) return "Unnamed";
    const lastDotIndex = fileName.lastIndexOf(".");
    const name = lastDotIndex !== -1 ? fileName.slice(0, lastDotIndex) : fileName;
    const extension = lastDotIndex !== -1 ? fileName.slice(lastDotIndex) : "";
    if (name.length <= 6) {
        return fileName;
    }
    const shortenedName = name.slice(0, 3) + ".." + name.slice(-2);
    return shortenedName + extension;
}

export function squeezeText(text, maxLength) {
    if (isEmpty(text)) return "";
    if (text.length <= maxLength) {
        return text;
    }
    const charsToShow = Math.floor((maxLength - 3) / 2);
    const start = text.slice(0, charsToShow);
    const end = text.slice(-charsToShow);
    return `${start}...${end}`;
}

export function formatNumber(value) {
    if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + 'm';
    } else if (value >= 1000) {
        return (value / 1000).toFixed(1) + 'k';
    }
    return value.toString();
}

export const convertToUserLocalTime = (date, timezone) => {
    if (isEmpty(date) || isEmpty(timezone)) {
        return '---';
    }
    return moment.tz(date, timezone).tz(moment.tz.guess()).format('YYYY-MM-DD HH:mm:ss');
};

export const getMode = () => {
    if (window.location.hostname === import.meta.env.VITE_ADMIN_HOSTNAME || import.meta.env.VITE_MODE === 'admin') {
        return 'admin';
    }
    return 'customer';
}

export function isIOS() {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
}

/**
 * Converts a duration value and unit into a total number of days.
 * Assumes 1 week = 7 days, 1 month = 30 days (adjust if needed).
 * @param {number|string} value The numeric duration value.
 * @param {'days'|'weeks'|'months'} unit The unit of duration.
 * @returns {number|null} The total duration in days, or null if input is invalid.
 */
export const convertToDays = (value, unit) => {
    const numericValue = Number(value);
    if (isNaN(numericValue) || numericValue < 0) {
        return null; // Invalid input value
    }

    switch (unit) {
        case 'days':
            return numericValue;
        case 'weeks':
            return numericValue * 7;
        case 'months':
            return numericValue * 30; // Approximation: 30 days per month
        default:
            return null; // Invalid unit
    }
};

// ... rest of existing functions ...
export const normalizeAndSortMessages = (data) => {
    const messages = data.map((item) => ({
        id: item.id,
        projectId: item.project_id,
        content: item.project_chat_contentable.content,
        createdAt: new Date(item.created_at),
        updatedAt: new Date(item.updated_at),
        type: item.project_chat_contentable_type,
    }));

    return messages.sort((a, b) => b.createdAt - a.createdAt);
};